import { Fragment, useEffect, useState } from 'react'
import { Box, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCheckSharedFolderAccess, useTargetPerson } from 'hooks'
import { Icon } from 'packages/eid-icons'
import {
    DifferentiationValueSelector as AccessLevelValueSelector,
    ItemDetails,
} from 'components'
import { useIsSmallScreen } from 'packages/core'
import cartHelpers from 'containers/Cart/cartHelpers'
import { OverviewSection } from './OverviewSection'
import { MoreInformationSection } from './MoreInformationSection'
import { AddToCart } from 'components/AddToCart'
import { ResourceTypesNamespace, useRegistry } from 'core'
import { Skeleton } from '@mui/material'
import { PreApprovedMembershipAccess } from 'components/PreApprovedItems/MembershipBasedAccess'
import appConfig from 'config'
import { useHistory } from 'react-router'
import useConfiguration from 'useConfiguration'
import ActivateNowButton from '../ActivateNowButton'
import TextWithIcon from 'components/common/TextWithIcon'

const sharedFolderPrimaryTabExplain = {
    overview:
        'Overview tab: shared folder display name, path, UNC path, hosting system, description, policy attributes when shown, and any published instructions.',
    additionalInformation:
        'More information tab: owners and responsible party for this shared folder when your permissions allow.',
}

const PromptContainer = styled('div')({
    padding: '48px 31px 20px 31px',
    minHeight: '232px',
    display: 'flex',
})

const PaddedDiv = styled(Box)({
    padding: '0 31px',
})

const SharedFolderDetails = ({ sharedFolder, toggleDrawer }) => {
    const { t } = useTranslation()
    const history = useHistory()

    const registry = useRegistry()

    const sharedFolderType = registry.get(
        ResourceTypesNamespace,
        'SharedFolders',
    )

    const { getResourceType } = useConfiguration()

    const currentResourceType = getResourceType('SharedFolders')

    const isSmallScreen = useIsSmallScreen()

    const [targetPerson] = useTargetPerson()

    const [activeTab, setActiveTab] = useState('overview')

    const { data: accessLevels, isInitialLoading: isCheckingAccess } =
        useCheckSharedFolderAccess(sharedFolder.id, targetPerson.id)
    const [accessLevel, setAccessLevelValue] = useState(null)

    useEffect(() => {
        if (accessLevel && accessLevels.length === 1) {
            setAccessLevelValue(accessLevels[0])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessLevels, setAccessLevelValue])

    const preAdd = (baseObj) => {
        const itemToAdd = cartHelpers.sharedFolderToCartItem({
            targetPerson,
            assignmentType: 'Add',
            sharedFolder,
            assignmentStatus: accessLevel,
            ...baseObj,
        })
        if (!itemToAdd) return undefined

        return itemToAdd
    }

    const getPreValidationMessage = () => {
        if (!Boolean(accessLevel)) {
            return t('Common_SelectAccessLevel')
        } else if (accessLevel && accessLevel.isAssigned) {
            return t('ResourceAlreadyAssigned')
        }
        return ''
    }

    return (
        <Fragment>
            <ItemDetails.Tabs
                variant="standard"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
            >
                <ItemDetails.Tabs.Tab
                    value="overview"
                    label={t('Common_Overview')}
                    data-ai-explain={sharedFolderPrimaryTabExplain.overview}
                />
                <ItemDetails.Tabs.Tab
                    value="additionalInformation"
                    label={t('Common_MoreInformation')}
                    data-ai-explain={
                        sharedFolderPrimaryTabExplain.additionalInformation
                    }
                />
            </ItemDetails.Tabs>
            {activeTab === 'overview' && (
                <OverviewSection sharedFolder={sharedFolder} />
            )}
            {activeTab === 'additionalInformation' && (
                <MoreInformationSection sharedFolder={sharedFolder} />
            )}
            {accessLevels &&
                accessLevels.filter((a) => a.isAssigned).length > 0 && (
                    <Box
                        data-ai-explain="Existing shared folder assignments: links to manage access or view assignments already granted for this folder."
                    >
                        <PreApprovedMembershipAccess
                            handleViewRequest={() => {
                                history.push(
                                    `${appConfig.APP_SUBPATH}/sharedfolders`,
                                )
                            }}
                            personResourceAssignments={accessLevels.filter(
                                (a) => a.isAssigned,
                            )}
                            resource={sharedFolder}
                            resourceType={sharedFolderType}
                            title={t('Common_ManageSharedFoldersAssignments')}
                            manageBtnUrl={'sharedFolders/manageAccess'}
                        />
                    </Box>
                )}
            {!isSmallScreen && (
                <PromptContainer>
                    <Box
                        width="172px"
                        display="flex"
                        flexDirection="column"
                        zIndex={1}
                        data-ai-explain="Shared folder access level (desktop): summary of the task; pick an available access package in the selector beside this column before add to cart or activate."
                    >
                        <Typography
                            style={{
                                fontSize: '14px',
                                lineHeight: 1.43,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                wordBreak: 'break-word',
                            }}
                        >
                            {t('Common_SelectAccessLevel')}
                        </Typography>
                        <Box
                            marginTop="24px"
                            width="100%"
                            display="flex"
                            justifyContent="center"
                        >
                            <Icon
                                name="AccessLevel"
                                height="150px"
                                width="150px"
                                color="#D2D2D9"
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{ display: 'flex', gap: '3.2rem' }}
                        data-ai-explain="Choose a shared folder access level; optional catalog instructions appear when the selected level includes guidance text."
                    >
                        <Box width="334px" paddingLeft="16px">
                            <AccessLevelValueSelector
                                loading={!accessLevels}
                                onChange={(val) => setAccessLevelValue(val)}
                                data={
                                    accessLevels &&
                                    accessLevels.filter((o) => !o.isAssigned)
                                }
                                value={
                                    accessLevel?.resourceAssignment
                                        .resourceAssignmentId
                                }
                                getOptionLabel={(o) =>
                                    o.resourceAssignment.friendlyName
                                }
                                checkSelectedOption={(o, selectedValueId) =>
                                    o.resourceAssignment
                                        .resourceAssignmentId ===
                                    selectedValueId
                                }
                            />
                        </Box>
                        {accessLevel?.instructions && (
                            <Box
                                data-ai-explain="Access-level-specific instructions for the shared folder option you selected."
                            >
                                <TextWithIcon
                                    iconName={'InfoNew'}
                                    message={accessLevel?.instructions}
                                />
                            </Box>
                        )}
                    </Box>
                </PromptContainer>
            )}

            {isSmallScreen && (
                <PaddedDiv>
                    <Box
                        paddingY="24px"
                        display="flex"
                        flexDirection="column"
                        position="relative"
                        data-ai-explain="Shared folder access level (mobile): select an available access package from the list before add to cart or activate."
                    >
                        <Box position="absolute" top="16px" right="0">
                            <Icon
                                name="AccessLevel"
                                height="100px"
                                width="100px"
                                color="#D2D2D9"
                            />
                        </Box>
                        <Box width="172px" paddingY="4px" zIndex={1}>
                            <Typography
                                style={{
                                    fontSize: '14px',
                                    lineHeight: 1.43,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {t('Common_SelectAccessLevel')}
                            </Typography>
                        </Box>
                        <Box paddingY="16px">
                            <AccessLevelValueSelector
                                loading={!accessLevels}
                                onChange={(val) => setAccessLevelValue(val)}
                                data={
                                    accessLevels &&
                                    accessLevels.filter((o) => !o.isAssigned)
                                }
                                value={
                                    accessLevel?.resourceAssignment
                                        .resourceAssignmentId
                                }
                                getOptionLabel={(o) =>
                                    o.resourceAssignment.friendlyName
                                }
                                checkSelectedOption={(o, selectedValueId) =>
                                    o.resourceAssignment
                                        .resourceAssignmentId ===
                                    selectedValueId
                                }
                            />
                        </Box>
                    </Box>
                </PaddedDiv>
            )}

            {isCheckingAccess ? (
                <Box
                    padding={'24px'}
                    data-ai-explain="Checking whether this shared folder access level can be requested or activated for the selected person."
                >
                    <Skeleton height={40} />
                </Box>
            ) : accessLevel?.canActivateNow ? (
                <ActivateNowButton
                    resource={sharedFolder}
                    resourceType={currentResourceType}
                    assignment={accessLevel}
                    forDetailsDrawer={true}
                    width="300px"
                />
            ) : (
                <Box
                    data-ai-explain="Add this shared folder access level to the cart to start an access request (pick a level that is not already assigned)."
                >
                    <AddToCart
                        resourceType={sharedFolderType}
                        resource={sharedFolder}
                        secondary={accessLevel}
                        preValidate={getPreValidationMessage}
                        preAdd={preAdd}
                        postAdd={toggleDrawer}
                    />
                </Box>
            )}
        </Fragment>
    )
}

export default SharedFolderDetails
